import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  setDoc
} from 'firebase/firestore';
import EditableFeedback from '../teacher/EditableFeedback';

const AdminStudentDashboard = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [lessonSubmissions, setLessonSubmissions] = useState([]);
  const [writingsData, setWritingsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const studentRef = doc(db, 'users', studentId);
        const studentSnap = await getDoc(studentRef);

        if (!studentSnap.exists()) {
          setLoading(false);
          return;
        }
        setStudent(studentSnap.data());

        // Fetch Quiz Data
        const lessonsSubRef = collection(db, 'users', studentId, 'lessons');
        const lessonsSubSnap = await getDocs(lessonsSubRef);
        setLessonSubmissions(lessonsSubSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Fetch Writing Data
        const writingsRef = collection(db, 'users', studentId, 'writings');
        const writingsSnap = await getDocs(writingsRef);
        setWritingsData(writingsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      } catch (error) {
        console.error('❌ Error loading student data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [studentId]);

  const handleSaveFeedback = async (writingId, updatedFeedback) => {
    try {
      const writingRef = doc(db, 'users', studentId, 'writings', writingId);
      await updateDoc(writingRef, {
        feedback: updatedFeedback,
        updatedAt: new Date()
      });

      const notificationRef = doc(collection(db, 'users', studentId, 'notifications'));
      await setDoc(notificationRef, {
        type: 'feedback',
        message: `Admin provided new feedback on ${writingId}.`,
        date: new Date(),
        read: false
      });

      setWritingsData(prev => prev.map(w => w.id === writingId ? { ...w, feedback: updatedFeedback } : w));
      alert('✅ Feedback saved!');
    } catch (error) {
      console.error('❌ Error:', error);
    }
  };

  if (loading) return <div style={styles.loading}>Loading student profile...</div>;

  // Numerical sorting logic for lessons
  const allLessonIds = [...new Set([...lessonSubmissions.map(l => l.id), ...writingsData.map(w => w.id)])]
    .sort((a, b) => parseInt(a.replace(/\D/g, '')) - parseInt(b.replace(/\D/g, '')));

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate('/admin/users')}>← Back to Users</button>
        <div style={styles.studentInfo}>
          <h1 style={styles.title}>{student.name || 'Unnamed'}'s Journey</h1>
          <span style={styles.emailBadge}>{student.email}</span>
        </div>
      </header>

      {allLessonIds.map(lid => {
        const quiz = lessonSubmissions.find(l => l.id === lid);
        const writing = writingsData.find(w => w.id === lid);

        return (
          <div key={lid} style={styles.lessonCard}>
            <h2 style={styles.lessonTitle}>Lesson {lid.replace(/\D/g, '')}</h2>
            
            <div style={styles.contentGrid}>
              {/* 1. QUIZ ANALYSIS */}
              <div style={styles.section}>
                <h3 style={styles.sectionHeading}>📖 Reading Quiz</h3>
                {quiz?.readingPassage?.questions ? (
                  <div style={styles.quizList}>
                    {quiz.readingPassage.questions.map((q, idx) => (
                      <div key={idx} style={styles.quizItem}>
                        <span style={{...styles.statusDot, backgroundColor: q.isCorrect ? '#10b981' : '#ef4444'}} />
                        <div style={styles.quizText}>
                          <strong>Q{q.questionId}:</strong> {Array.isArray(q.userAnswer) ? q.userAnswer.join(', ') : q.userAnswer}
                          {q.attemptHistory && q.attemptHistory.length > 1 && (
                            <div style={styles.historyText}>History: {q.attemptHistory.join(' → ')}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p style={styles.dimText}>No quiz data.</p>}
              </div>

              {/* 2. C-TEST ANALYSIS */}
              <div style={styles.section}>
                <h3 style={styles.sectionHeading}>🧠 Vocab Recall</h3>
                {writing?.vocabRecall?.originalText ? (
                  <div style={styles.cTestBox}>
                    {writing.vocabRecall.originalText.split('\n').map((para, pIdx) => (
                      <p key={pIdx} style={styles.cTestPara}>
                        {para.split(/(\w+\[\w+\])/g).map((part, wIdx) => {
                          const match = part.match(/(\w+)\[(\w+)\]/);
                          if (match) {
                            const suffix = match[2].toLowerCase();
                            const userAns = writing.vocabRecall.answers?.[suffix] || '___';
                            const isCorrect = userAns.toLowerCase() === suffix;
                            const mistakes = writing.vocabRecall.mistakeCount?.[suffix] || 0;
                            return (
                              <span key={wIdx} style={{ color: isCorrect ? '#059669' : '#dc2626', fontWeight: 'bold' }}>
                                {match[1]}[{userAns}]
                                {mistakes > 0 && <sub style={styles.errSub}>({mistakes} err)</sub>}
                              </span>
                            );
                          }
                          return part;
                        })}
                      </p>
                    ))}
                  </div>
                ) : <p style={styles.dimText}>No vocab recall data.</p>}
              </div>
            </div>

            {/* 3. WRITING & FEEDBACK */}
            <div style={styles.writingSection}>
              <h3 style={styles.sectionHeading}>✍️ Final Writing</h3>
              {writing?.writingText ? (
                <>
                  <div style={styles.essayBox}>{writing.writingText}</div>
                  <div style={styles.feedbackContainer}>
                    <p style={styles.label}>Admin/Teacher Feedback:</p>
                    <EditableFeedback 
                      initialContent={writing.feedback || ''} 
                      onSave={(val) => handleSaveFeedback(writing.id, val)} 
                    />
                  </div>
                </>
              ) : <p style={styles.dimText}>Writing submission pending.</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  container: { padding: '40px 20px', maxWidth: '1100px', margin: '0 auto', backgroundColor: '#f8fafc', minHeight: '100vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', borderBottom: '2px solid #e2e8f0', paddingBottom: '20px' },
  backButton: { padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
  studentInfo: { textAlign: 'right' },
  title: { margin: 0, color: '#1e3a8a', fontFamily: 'Merriweather, serif', fontSize: '1.8rem' },
  emailBadge: { backgroundColor: '#eff6ff', color: '#1e40af', padding: '2px 10px', borderRadius: '20px', fontSize: '0.85rem' },
  lessonCard: { backgroundColor: '#fff', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', marginBottom: '30px', border: '1px solid #e2e8f0' },
  lessonTitle: { margin: '0 0 20px 0', color: '#1e3a8a', borderLeft: '4px solid #3b82f6', paddingLeft: '15px' },
  contentGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px' },
  section: { backgroundColor: '#fdfdfd', padding: '20px', borderRadius: '10px', border: '1px solid #f1f5f9' },
  sectionHeading: { fontSize: '1rem', color: '#475569', marginBottom: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' },
  quizList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  quizItem: { display: 'flex', gap: '10px', fontSize: '0.9rem' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%', marginTop: '6px', flexShrink: 0 },
  historyText: { fontSize: '0.75rem', color: '#f59e0b', fontStyle: 'italic' },
  cTestBox: { fontSize: '0.9rem', lineHeight: '1.6', color: '#334155' },
  cTestPara: { marginBottom: '10px' },
  errSub: { fontSize: '0.65rem', marginLeft: '2px', color: '#94a3b8' },
  writingSection: { borderTop: '1px solid #f1f5f9', paddingTop: '20px' },
  essayBox: { backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', whiteSpace: 'pre-wrap', border: '1px solid #e2e8f0', color: '#1e293b', marginBottom: '20px' },
  label: { fontWeight: 'bold', fontSize: '0.9rem', color: '#1e3a8a', marginBottom: '10px' },
  dimText: { color: '#94a3b8', fontStyle: 'italic' },
  loading: { textAlign: 'center', marginTop: '100px', color: '#64748b' }
};

export default AdminStudentDashboard;